/**
 * 브라우저 호환성이 좋은 AudioContext 생성 유틸리티
 * iOS Safari 및 구형 브라우저 대응
 */

export interface AudioContextLike {
  state: string;
  resume(): Promise<void>;
  suspend(): Promise<void>;
  close(): Promise<void>;
  decodeAudioData(
    audioData: ArrayBuffer
  ): Promise<AudioBuffer>;
  createAnalyser(): AnalyserNode;
  sampleRate: number;
}

/**
 * 안전한 AudioContext 생성
 * @returns AudioContext 또는 null
 */
export function createAudioContext(): AudioContextLike | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    // 브라우저별 AudioContext 생성자 확인
    const AudioContextCtor =
      (window as { AudioContext?: typeof AudioContext }).AudioContext ||
      (window as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;

    if (!AudioContextCtor) {
      console.error("❌ 브라우저가 Web Audio API를 지원하지 않습니다");
      return null;
    }

    // AudioContext 생성
    const audioContext = new AudioContextCtor();

    // iOS Safari에서 자동 재생 정책 대응
    if (audioContext.state === "suspended") {
      const resumeAudioContext = async () => {
        try {
          await audioContext.resume();
          console.log("✅ AudioContext가 재개되었습니다");
        } catch (error) {
          console.error("❌ AudioContext 재개 실패:", error);
        }
      };

      // 사용자 상호작용 시 재개
      const events = ["click", "touchstart", "touchend"];
      events.forEach(event => {
        document.addEventListener(
          event,
          resumeAudioContext,
          { once: true }
        );
      });
    }

    console.log("✅ AudioContext 생성 성공:", {
      sampleRate: audioContext.sampleRate,
      state: audioContext.state,
    });

    return audioContext as unknown as AudioContextLike;
  } catch (error) {
    console.error("❌ AudioContext 생성 실패:", error);
    return null;
  }
}

/**
 * 안전한 OfflineAudioContext 생성
 * @param numberOfChannels 채널 수
 * @param length 길이
 * @param sampleRate 샘플레이트
 * @returns OfflineAudioContext 또는 null
 */
export function createOfflineAudioContext(
  numberOfChannels: number,
  length: number,
  sampleRate: number
): OfflineAudioContext | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    // 브라우저별 OfflineAudioContext 생성자 확인
    const OfflineAudioContextCtor =
      (window as { OfflineAudioContext?: typeof OfflineAudioContext })
        .OfflineAudioContext ||
      (window as { webkitOfflineAudioContext?: typeof OfflineAudioContext })
        .webkitOfflineAudioContext;

    if (!OfflineAudioContextCtor) {
      console.error(
        "❌ 브라우저가 OfflineAudioContext를 지원하지 않습니다"
      );
      return null;
    }

    const offlineContext = new OfflineAudioContextCtor(
      numberOfChannels,
      length,
      sampleRate
    );

    console.log("✅ OfflineAudioContext 생성 성공:", {
      numberOfChannels,
      length,
      sampleRate,
    });

    return offlineContext;
  } catch (error) {
    console.error("❌ OfflineAudioContext 생성 실패:", error);
    return null;
  }
}

/**
 * 브라우저/디바이스 정보 가져오기
 */
export function getBrowserInfo(): {
  isIOS: boolean;
  isAndroid: boolean;
  isSafari: boolean;
  isChrome: boolean;
  supportsWebAudio: boolean;
  supportsOfflineAudio: boolean;
  userAgent: string;
} {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      isIOS: false,
      isAndroid: false,
      isSafari: false,
      isChrome: false,
      supportsWebAudio: false,
      supportsOfflineAudio: false,
      userAgent: "",
    };
  }

  const userAgent = navigator.userAgent;

  return {
    isIOS: /iPhone|iPad|iPod/.test(userAgent),
    isAndroid: /Android/.test(userAgent),
    isSafari: /Safari/.test(userAgent) && !/Chrome/.test(userAgent),
    isChrome: /Chrome/.test(userAgent) && !/Chromium/.test(userAgent),
    supportsWebAudio:
      typeof (window as { AudioContext?: unknown }).AudioContext !==
        "undefined" ||
      typeof (window as { webkitAudioContext?: unknown })
        .webkitAudioContext !== "undefined",
    supportsOfflineAudio:
      typeof (window as { OfflineAudioContext?: unknown })
        .OfflineAudioContext !== "undefined" ||
      typeof (window as { webkitOfflineAudioContext?: unknown })
        .webkitOfflineAudioContext !== "undefined",
    userAgent,
  };
}

